import { useMemo, useState } from "react";
import { Banknote, X } from "lucide-react";
import { money } from "../lib/pos";

/**
 * Cash tender step — cashier types the amount received from the guest,
 * modal shows the change to return, then "Done" hands `method="cash"` back
 * to the parent (which posts the payment + prints the bill).
 *
 * Props:
 *   total    — order total due (₹)
 *   onCancel — close without paying
 *   onDone   — invoked with no args once cashier confirms
 */
export default function CashTenderModal({ total, onCancel, onDone }) {
  const [tender, setTender] = useState("");
  const [busy, setBusy] = useState(false);

  const parsed = parseFloat(tender);
  const received = Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  const change = Math.max(0, +(received - total).toFixed(2));
  const short = Math.max(0, +(total - received).toFixed(2));
  const enough = received >= total;

  // Handy quick-tender chips: exact, next ₹100 up, next ₹500 up
  const suggestions = useMemo(() => {
    const seen = new Set();
    const out = [];
    const push = (v) => { const k = v.toFixed(2); if (!seen.has(k)) { seen.add(k); out.push(v); } };
    push(Math.round(total * 100) / 100);
    push(Math.ceil(total / 100) * 100);
    push(Math.ceil(total / 500) * 500);
    return out;
  }, [total]);

  const submit = async () => {
    if (!enough || busy) return;
    setBusy(true);
    try { await onDone(); } finally { setBusy(false); }
  };

  return (
    <div className="modal-backdrop" onClick={(e) => e.target.classList.contains("modal-backdrop") && onCancel()}>
      <div className="modal cash-tender" style={{ width: "min(440px, 92vw)" }} data-testid="cash-tender-modal">
        <div className="section-line">
          <h2><Banknote size={18} style={{ verticalAlign: "-3px", marginRight: 8 }} />Cash tender</h2>
          <button data-testid="cash-tender-close" onClick={onCancel}><X size={18} /></button>
        </div>
        <div className="collect-total"><span>AMOUNT DUE</span><b data-testid="cash-tender-due">{money(total)}</b></div>

        <label className="field" style={{ marginTop: 4 }}>
          <span style={{ fontSize: 11, color: "var(--muted)", letterSpacing: 1.5, textTransform: "uppercase" }}>Money received</span>
          <input
            data-testid="cash-tender-input"
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            autoFocus
            placeholder="0.00"
            value={tender}
            onChange={(e) => setTender(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
            style={{ fontSize: 22, fontWeight: 700, padding: "12px 14px", textAlign: "right" }}
          />
        </label>

        <div className="cash-suggests" style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {suggestions.map((v) => (
            <button
              key={v}
              type="button"
              className="outline"
              data-testid={`cash-tender-quick-${v}`}
              onClick={() => setTender(String(v))}
              style={{ flex: 1, minWidth: 90 }}
            >{money(v)}</button>
          ))}
        </div>

        <div
          className={`cash-change ${enough ? "ok" : "short"}`}
          data-testid="cash-tender-change"
          style={{
            marginTop: 6,
            padding: "14px 16px",
            borderRadius: 4,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: enough ? "#14341f" : "#3a1a1a",
            color: enough ? "var(--green)" : "#ff8f8a",
            border: `1px solid ${enough ? "#2b6b3d" : "#7a2a2a"}`,
          }}
        >
          <span style={{ fontSize: 11, letterSpacing: 1.5, textTransform: "uppercase" }}>
            {enough ? "Change to return" : "Short by"}
          </span>
          <b style={{ fontSize: 26, fontFamily: "Barlow Condensed" }}>
            {money(enough ? change : short)}
          </b>
        </div>

        <button
          data-testid="cash-tender-done"
          className="primary wide"
          disabled={!enough || busy}
          onClick={submit}
        >{busy ? "Processing…" : "Done · Confirm payment"}</button>
      </div>
    </div>
  );
}
